import { useBottomSheetInternal } from '@gorhom/bottom-sheet';
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import type {
  NativeSyntheticEvent,
  TargetedEvent,
  TextInput,
} from 'react-native';

import composeRefs from './composeRefs';
import { findNodeHandle } from './findNodeHandler.web';

/**
 * HOC that adds bottom sheet keyboard handling to any input component
 */
export const withBottomSheetInput = <
  P extends {
    onFocus?: (event: NativeSyntheticEvent<TargetedEvent>) => void;
    onBlur?: (event: NativeSyntheticEvent<TargetedEvent>) => void;
  },
>(
  WrappedInput: React.ComponentType<P>,
) => {
  const WithBottomSheetInput = forwardRef<TextInput, P>((props, ref) => {
    const internalRef = useRef<TextInput>(null);

    const { animatedKeyboardState, textInputNodesRef } =
      useBottomSheetInternal();

    const { onFocus, onBlur, ...rest } = props;

    const handleOnFocus = useCallback(
      (args: NativeSyntheticEvent<TargetedEvent>) => {
        const targetNodeHandle = findNodeHandle(internalRef.current);
        if (targetNodeHandle) {
          animatedKeyboardState.set((state) => ({
            ...state,
            target: targetNodeHandle as number,
          }));
        }

        if (onFocus) {
          onFocus(args);
        }
      },
      [onFocus, animatedKeyboardState],
    );

    const handleOnBlur = useCallback(
      (args: NativeSyntheticEvent<TargetedEvent>) => {
        const keyboardState = animatedKeyboardState.get();
        const currentFocusedInput = findNodeHandle(internalRef.current);

        /**
         * we need to make sure that we only remove the target
         * if the target belong to the current component and
         * if the currently focused input is not in the targets set.
         */
        const shouldRemoveCurrentTarget =
          currentFocusedInput !== null &&
          keyboardState.target === (currentFocusedInput as number);

        const shouldIgnoreBlurEvent =
          currentFocusedInput &&
          textInputNodesRef.current.has(currentFocusedInput as number);

        if (shouldRemoveCurrentTarget && !shouldIgnoreBlurEvent) {
          animatedKeyboardState.set((state) => ({
            ...state,
            target: undefined,
          }));
        }

        if (onBlur) {
          onBlur(args);
        }
      },
      [onBlur, animatedKeyboardState, textInputNodesRef],
    );

    useEffect(() => {
      const componentNode = findNodeHandle(internalRef.current) as number;
      if (!componentNode) {
        return;
      }

      const textInputNodesRefValue = textInputNodesRef.current;
      if (!textInputNodesRefValue.has(componentNode)) {
        textInputNodesRefValue.add(componentNode);
      }

      return () => {
        const _componentNode = componentNode; // findNodeHandle(internalRef.current) as number;
        if (!_componentNode) {
          return;
        }

        const keyboardState = animatedKeyboardState.get();
        /**
         * remove the keyboard state target if it belong
         * to the current component.
         */
        if (keyboardState.target === _componentNode) {
          animatedKeyboardState.set((state) => ({
            ...state,
            target: undefined,
          }));
        }

        if (textInputNodesRefValue.has(_componentNode)) {
          textInputNodesRefValue.delete(_componentNode);
        }
      };
    }, [textInputNodesRef, animatedKeyboardState]);

    return (
      <WrappedInput
        {...(rest as unknown as P)}
        ref={composeRefs(ref, internalRef)}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
      />
    );
  });

  WithBottomSheetInput.displayName = `withBottomSheetInput(${
    WrappedInput.displayName || WrappedInput.name || 'Component'
  })`;

  return WithBottomSheetInput;
};
